import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { ProjectsLayout } from "@/components/layouts";
import { PlusIcon, FolderIcon } from "@heroicons/react/24/solid";
import { CreateProjectModal } from "@/features/projects/modals/create-project-modal";
import { useGetAccountStudies } from "@/api/studio/studies/queries";
import { useModalStore } from "@/stores/use-modal-store";
import { asRelativeDate } from "@/utils/format";
import { getStudyTypeCountLabel } from "@/utils/study-type-label";
import { Study } from "@/api/studio/studies/types/study.type";
import { StudiesSkeleton } from "@/features/studies/components/studies-skeleton";
import { StudyDropdown } from "@/features/studies/components/study-dropdown";
import { Pagination } from "@/components/ui/pagination";
import { Search } from "@/components/ui/search";
import { Button } from "@/components/ui/button";
import { Table, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { StudyIcon } from "@/features/studies/components/study-icon";
import { dynamicUseCase } from "@/utils/dynamic-use-case";
import { EmptyState } from "@/features/projects/components/empty-state";
import { GetStudiesReq } from "@/api/studio/studies/api";
import { useSegment } from "@/hooks/use-segment";
import { useGlobalStore } from "@/stores/global-store";
import { SelectSort } from "@/components/ui/select";
import { sanitizeStudyRoute } from "@/utils/study-route";

/*
 * StudiesRoute component
 * Lists all studies for an account
 */
export const StudiesRoute = () => {
  /* Stores */
  const { open } = useModalStore((state) => state.modalActions);
  const [, navigate] = useLocation();
  const theme = useGlobalStore((state) => state.theme);

  /* Hooks */
  const { segmentPageEvent } = useSegment();

  /* State */
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState<GetStudiesReq["sortBy"]>("desc");
  const [orderBy, setOrderBy] = useState<GetStudiesReq["orderBy"]>("updatedAt");
  const [pageIndex, setPageIndex] = useState(1);

  /* Queries */
  const { data: studies, isLoading: isStudiesLoading } = useGetAccountStudies({
    query,
    pageIndex,
    itemsPerPage: 10,
    sortBy,
    orderBy,
  });

  /* Handlers */
  const handleStudyOpen = (study: Study) => {
    const validRoute = sanitizeStudyRoute(study.route);
    navigate(`/projects/${study.project.id}/studies/${study.id}/${study.type}/${validRoute}`);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Reset to first page on search
    setPageIndex(1);
    setQuery(e.target.value);
  };

  /* Side effects */
  useEffect(() => {
    // segment page event when a user visits the project page
    if (theme)
      segmentPageEvent("Studies", {
        title: `Studies | Dragonfly AI Studio`,
        theme: theme,
      });
  }, []);

  /* Render */
  return (
    <ProjectsLayout>
      {/* Primary */}
      <Toolbar>
        <ToolbarItem>
          <h1 className="font-semibold">Studies</h1>
        </ToolbarItem>
        <ToolbarItem>
          <Search onChange={handleSearch} />
          <Button variant="primary" onClick={() => open(<CreateProjectModal />)}>
            <PlusIcon className="size-5" /> New project
          </Button>
        </ToolbarItem>
      </Toolbar>

      {/* Tertiary */}
      <Toolbar>
        <ToolbarItem>
          <h2>
            {studies?.maxRecordCount} {studies?.maxRecordCount && studies?.maxRecordCount < 2 ? "study" : "studies"}
          </h2>
        </ToolbarItem>
        <ToolbarItem>
          <SelectSort setOrderBy={setOrderBy} setSortBy={setSortBy} />
        </ToolbarItem>
      </Toolbar>

      {/* Skeleton Loader */}
      {isStudiesLoading && <StudiesSkeleton />}

      {/* Empty fallback */}
      {!isStudiesLoading && studies && studies.studies.length === 0 && <EmptyState title="This project has no studies" />}

      {/* Studies */}
      {!isStudiesLoading && studies && studies.studies.length !== 0 && (
        <Table>
          <thead>
            <TableRow>
              <TableHeader className="w-80">Study</TableHeader>
              <TableHeader>Project</TableHeader>
              <TableHeader>Use case</TableHeader>
              <TableHeader>Author</TableHeader>
              <TableHeader>Last edited</TableHeader>
              <TableHeader className="w-40"></TableHeader>
            </TableRow>
          </thead>
          <tbody>
            {studies.studies.map((study) => (
              <TableRow key={study.id} onClick={() => handleStudyOpen(study)}>
                <TableCell>
                  <StudyIcon useCase={study.useCase} />
                  <div className="flex flex-col gap-1 truncate">
                    <p className="font-semibold truncate">{study.name}</p>
                    <p className="text-sm text-base-content/60 truncate">
                      {study.assetCount} {getStudyTypeCountLabel(study.type, study.assetCount)}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <FolderIcon className="size-4 shrink-0" />
                  <p className="text-sm truncate">{study.project.name}</p>
                </TableCell>
                <TableCell>
                  {dynamicUseCase(study.useCase.name)}
                  <p className="text-sm truncate">{study.useCase.name}</p>
                </TableCell>
                <TableCell>
                  <p className="text-sm truncate">{study.username}</p>
                </TableCell>
                <TableCell>
                  <p className="text-sm truncate">{asRelativeDate(study.updatedAt)}</p>
                </TableCell>
                <TableCell className="justify-end" truncate={false}>
                  <StudyDropdown projectId={study.project.id} study={study} />
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      )}

      {/* Pagination */}
      <Pagination currentPage={pageIndex} totalPages={studies?.maxPageIndex ?? 1} onPageChange={setPageIndex} />
    </ProjectsLayout>
  );
};
